import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useKraCycleStore from '../../store/kraCycleStore';
import useUIStore from '../../store/uiStore';
import useAuthStore from '../../store/authStore';
import KRACycleNotes from '../../components/KRACycleNotes';
import ScrollX from '../../components/ScrollX';
import { ownScoreOutOf100 } from '../../utils/kraScore';

const ScoreSelector = ({ value, onChange, disabled }) => (
  <div className="flex gap-1">
    {[0, 1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={disabled}
        onClick={() => onChange(n)}
        className={`w-7 h-7 rounded text-xs font-bold border ${
          value === n ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white text-gray-500 border-gray-300 hover:border-komal-blue'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {n}
      </button>
    ))}
  </div>
);

const DiffBadge = ({ self, hod }) => {
  if (self == null || hod == null) return null;
  const diff = Math.abs(self - hod);
  if (diff === 0) return null;
  return (
    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${diff >= 2 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
      Δ{diff}
    </span>
  );
};

const TeamReviewDetailPage = () => {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const { selectedCycle, isLoading, isSubmitting, fetchCycle, saveHodResponses, submitHodResponses, reopenCycle, deleteCycle } = useKraCycleStore();
  const role = useAuthStore((s) => s.user?.role);
  const showToast = useUIStore((s) => s.showToast);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});

  useEffect(() => {
    fetchCycle(cycleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  useEffect(() => {
    if (!selectedCycle) return;
    const s = {};
    const c = {};
    selectedCycle.items.forEach((item) => {
      s[item.kra_item_id] = item.hod_score;
      c[item.kra_item_id] = item.hod_comment || '';
    });
    setScores(s);
    setComments(c);
  }, [selectedCycle]);

  if (isLoading || !selectedCycle) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Loading...</p></div>;
  }

  const editable = ['ready_for_hod', 'external_pending', 'reopened'].includes(selectedCycle.status);
  const selfTotal = ownScoreOutOf100(selectedCycle.items, (item) => item.self_score);
  const hodTotal = ownScoreOutOf100(selectedCycle.items, (item) => (item.kra_item_id in scores ? scores[item.kra_item_id] : item.hod_score));

  const buildResponses = () =>
    selectedCycle.items.map((item) => ({
      kra_item_id: item.kra_item_id,
      score: scores[item.kra_item_id] ?? null,
      comment: comments[item.kra_item_id] || null,
    }));

  const handleSaveDraft = async () => {
    const result = await saveHodResponses(cycleId, buildResponses());
    showToast(result.success ? 'Saved' : result.error, result.success ? 'success' : 'error');
  };

  const handleSubmit = async () => {
    await saveHodResponses(cycleId, buildResponses());
    const result = await submitHodResponses(cycleId);
    if (result.success) {
      showToast('Review submitted, record locked', 'success');
      navigate('/kra/team');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleReopen = async () => {
    const reason = window.prompt('Reason for reopening this locked record (required, logged in the audit trail):');
    if (!reason || !reason.trim()) return;
    const result = await reopenCycle(cycleId, reason);
    showToast(result.success ? 'Record reopened' : result.error, result.success ? 'success' : 'error');
  };

  // Only HR / CEO may delete a record (the backend enforces this too). A locked
  // record has to be reopened first, so a finished review can't vanish in one click.
  const canDelete = (role === 'HR' || role === 'CEO') && selectedCycle.status !== 'locked';

  const handleDelete = async () => {
    const monthLabel = new Date(selectedCycle.month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const reason = window.prompt(
      `Permanently delete ${selectedCycle.employee_name}'s ${monthLabel} KRA record, with all its scores, cross inputs and notes?\n\nReason (required, logged in the audit trail):`
    );
    if (!reason || !reason.trim()) return;
    const result = await deleteCycle(cycleId, reason);
    if (result.success) {
      showToast(result.message || 'Record deleted', 'success');
      navigate('/kra/team');
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">{selectedCycle.employee_name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(selectedCycle.month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Self and HOD are each that person's own scores only (out of 100); the
            third card is the blended Self 30% / HOD 50% / External 20% total.
            Showing the blended number under all three labels made Self and HOD
            read as identical. HOD updates live from the form as scores are picked. */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="glass-card p-4 border-l-4 border-komal-blue">
            <div className="text-2xl font-bold text-gray-900">{selfTotal ?? '—'} <span className="text-sm font-normal text-gray-400">/ 100</span></div>
            <div className="text-xs text-gray-500 mt-1">Self Score</div>
          </div>
          <div className="glass-card p-4 border-l-4 border-amber-500">
            <div className="text-2xl font-bold text-gray-900">{hodTotal ?? '—'} <span className="text-sm font-normal text-gray-400">/ 100</span></div>
            <div className="text-xs text-gray-500 mt-1">HOD Score (live)</div>
          </div>
          <div className="glass-card p-4 border-l-4 border-green-500">
            <div className="text-2xl font-bold text-gray-900">{selectedCycle.final_score ?? selectedCycle.preview_score ?? '—'} <span className="text-sm font-normal text-gray-400">/ 100</span></div>
            <div className="text-xs text-gray-500 mt-1">{selectedCycle.final_score != null ? 'Final Score' : 'Final Score (preview)'}</div>
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">KRA</th>
                  <th className="px-5 py-2 font-medium">Weight</th>
                  <th className="px-5 py-2 font-medium">Self</th>
                  <th className="px-5 py-2 font-medium">External</th>
                  <th className="px-5 py-2 font-medium">HOD Score</th>
                  <th className="px-5 py-2 font-medium">Δ</th>
                </tr>
              </thead>
              <tbody>
                {selectedCycle.items.map((item) => (
                  <tr key={item.kra_item_id} className="border-b border-gray-50 last:border-0 align-top">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.kpi}</div>
                    </td>
                    <td className="px-5 py-3 font-semibold">{item.weightage}%</td>
                    <td className="px-5 py-3">{item.self_score ?? <span className="text-gray-400 italic text-xs">Pending</span>}</td>
                    <td className="px-5 py-3">
                      {item.requires_external
                        ? (item.external_score ?? <span className="text-amber-600 italic text-xs">Pending</span>)
                        : <span className="text-gray-400 italic text-xs">Evidence only</span>}
                    </td>
                    <td className="px-5 py-3">
                      <ScoreSelector value={scores[item.kra_item_id]} disabled={!editable} onChange={(n) => setScores((s) => ({ ...s, [item.kra_item_id]: n }))} />
                      {editable && (
                        <input
                          type="text"
                          placeholder="Comment"
                          value={comments[item.kra_item_id] || ''}
                          onChange={(e) => setComments((c) => ({ ...c, [item.kra_item_id]: e.target.value }))}
                          className="mt-2 w-full text-xs border border-gray-200 rounded px-2 py-1"
                        />
                      )}
                    </td>
                    <td className="px-5 py-3"><DiffBadge self={item.self_score} hod={scores[item.kra_item_id]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>

          <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
            {canDelete && (
              <button onClick={handleDelete} disabled={isSubmitting} className="mr-auto px-4 py-2 text-sm rounded border border-red-300 text-red-700 font-semibold disabled:opacity-60">
                Delete Record
              </button>
            )}
            {selectedCycle.status === 'locked' && (
              <button onClick={handleReopen} disabled={isSubmitting} className="px-4 py-2 text-sm rounded border border-red-300 text-red-700 font-semibold disabled:opacity-60">
                Reopen
              </button>
            )}
            {editable && (
              <>
                <button onClick={handleSaveDraft} disabled={isSubmitting} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 font-semibold disabled:opacity-60">
                  Save Draft
                </button>
                <button onClick={handleSubmit} disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                  Submit Review
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-6">
          <KRACycleNotes cycleId={cycleId} isOwnCycle={false} />
        </div>
      </div>
    </div>
  );
};

export default TeamReviewDetailPage;
